import {
  uuid,
  timestamp,
  boolean,
  numeric,
  varchar,
  jsonb,
  text,
  date,
  integer,
  bigint,
} from 'drizzle-orm/pg-core/columns';
import { pgTable } from 'drizzle-orm/pg-core/table';
import { foreignKey } from 'drizzle-orm/pg-core/foreign-keys';
import { index, uniqueIndex } from 'drizzle-orm/pg-core/indexes';
import { sql } from 'drizzle-orm/sql/sql';
import { identities } from './identities';
import { meetings, optionsRegistrations, shareIssues, snrBonds, snrCases, snrShareCapitalChanges } from './initial';
import { snrAuthorizations } from './initial';
import { companies } from './companies';
import { shareholders } from './shareholders';
import { InferSelectModel } from 'drizzle-orm/table';
import { files } from './files';

export interface ShareClass {
  name: string;
  voteWeight: string;
  quantity?: string;
}

export interface ShareClassWithMinMax extends ShareClass {
  min: string;
  max: string;
}

export enum ValuationMethod {
  Issue,
  Assets,
  ManualIssue,
  Other,
}

export enum ShareholdersAgreementKind {
  UseExistingAgreement,
  CreateAgreement,
  NoAgreement,
}

export const warrantProgramDocuments = pgTable(
  'warrant_program_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .notNull(),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    fileId: uuid('file_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxWpdFileid: uniqueIndex('idx_wpd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxWpdWpid: index('idx_wpd_wpid').using('btree', table.warrantProgramId.asc().nullsLast().op('uuid_ops')),
      },
    ];
  },
);

export enum BoardsStatementOption {
  None = 'None',
  Produce = 'Produce',
  Waiver = 'Waiver',
  WaivedByAuth = 'WaivedByAuth',
  ProducedExternally = 'ProducedExternally',
}

export const warrantPrograms = pgTable(
  'warrant_programs',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    companyId: uuid('company_id').notNull(),
    description: varchar().notNull(),
    name: varchar(),
    valuationCompanyValue: numeric('valuation_company_value'),
    valuationDate: date('valuation_date'),
    volatility: numeric(),
    volatilityDate: timestamp('volatility_date'),
    volatilityDocument: varchar('volatility_document'),
    strikePrice: numeric('strike_price'),
    meetingId: uuid('meeting_id'),
    snrBondId: uuid('snr_bond_id'),
    paymentAccountNumber: varchar('payment_account_number'),
    invoiceAddress: jsonb('invoice_address'),
    serviceKind: integer('service_kind'),
    bolagsverketPaidDate: date('bolagsverket_paid_date'),
    payBeforeDate: date('pay_before_date'),
    warrantStartDate: date('warrant_start_date'),
    shareSubscribeStartDate: date('share_subscribe_start_date'),
    shareSubscribeEndDate: date('share_subscribe_end_date'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    warrantKind: integer('warrant_kind').notNull(),
    valuationMethod: integer('valuation_method').$type<ValuationMethod>(),
    valuationSccId: uuid('valuation_scc_id'),
    shareholdersAgreementKind: integer('shareholders_agreement_kind').$type<ShareholdersAgreementKind>(),
    strikePriceFromQuotaValue: boolean('strike_price_from_quota_value'),
    approvedByMeeting: boolean('approved_by_meeting'),
    signatoryId: uuid('signatory_id'),
    contactId: uuid('contact_id'),
    warrantEndDate: date('warrant_end_date'),
    primaryLang: boolean('primary_lang').default(true).notNull(),
    secondaryLang: boolean('secondary_lang').default(false).notNull(),
    riskfreeInterestRate: numeric('riskfree_interest_rate'),
    delRiksbankenRates: jsonb('__del_riksbanken_rates'),
    useRiksbankenRate: boolean('use_riksbanken_rate'),
    pricePerWarrant: numeric('price_per_warrant'),
    paymentAccountKind: integer('payment_account_kind').notNull(),
    ibanNumber: varchar('iban_number'),
    ibanBic: varchar('iban_bic'),
    ibanBank: varchar('iban_bank'),
    ibanAddress: varchar('iban_address'),
    warrantSubscribeEndDate: date('warrant_subscribe_end_date'),
    delBoardsStatementOption: integer('__del_boards_statement_option'),
    boardsStatementDate: date('boards_statement_date'),
    boardsStatementSv: varchar('boards_statement_sv'),
    boardsStatementEn: varchar('boards_statement_en'),
    illiquidityDiscount: numeric('illiquidity_discount').notNull(),
    discountPricePerWarrant: numeric('discount_price_per_warrant'),
    snrCaseId: uuid('snr_case_id'),
    valuationNumberOfShares: numeric('valuation_number_of_shares'),
    usesStepper: boolean('uses_stepper').notNull(),
    snrAuthorizationId: uuid('snr_authorization_id'),
    paymentName: varchar('payment_name'),
    valuationShareIssueId: uuid('valuation_share_issue_id'),
    paidForAt: timestamp('paid_for_at', { mode: 'string' }),
    stripeInvoiceId: varchar('stripe_invoice_id'),
    paymentKindCash: boolean('payment_kind_cash').notNull(),
    paymentKindOffset: boolean('payment_kind_offset').notNull(),
    paymentKindNonCash: boolean('payment_kind_non_cash').notNull(),
    maxNumberOfOptions: numeric('max_number_of_options'),
    nonCashStatementPl: varchar('non_cash_statement_pl'),
    nonCashStatementSl: varchar('non_cash_statement_sl'),
    shareClasses: jsonb('share_classes').$type<ShareClassWithMinMax[]>().default([]).notNull(),
    wiseFullyDiluted: boolean('wise_fully_diluted'),
    wiseDepBondsRecordDay: date('wise_dep_bonds_record_day'),
    wiseCeil: numeric('wise_ceil'),
    wiseFloor: numeric('wise_floor'),
    antlerWise: boolean('antler_wise').default(false).notNull(),
    warrantsForQeso: boolean('warrants_for_qeso').default(false).notNull(),
    minRoundSizeAmount: numeric('min_round_size_amount'),
    currency: varchar(),
    useNetStrike: boolean('use_net_strike').notNull(),
    riksbankenRatesSeriesId: varchar('riksbanken_rates_series_id'),
    riksbankenRates: jsonb('riksbanken_rates'),
    forEmployees: boolean('for_employees').notNull(),
    forBoardMembers: boolean('for_board_members').notNull(),
    forAdvisors: boolean('for_advisors'),
    forInvestors: boolean('for_investors').notNull(),
    boardsStatementOption: integer('boards_statement_option').$type<BoardsStatementOption>(),
    archived: boolean().default(false),
    subscriberAccess: boolean('subscriber_access').default(false),
  },
  table => {
    return [
      {
        idxWpCid: index('idx_wp_cid').using('btree', table.companyId.asc().nullsLast().op('uuid_ops')),
        idxWpCidKindDescription: uniqueIndex('idx_wp_cid_kind_description').using(
          'btree',
          table.companyId.asc().nullsLast().op('text_ops'),
          table.warrantKind.asc().nullsLast().op('int4_ops'),
          table.description.asc().nullsLast().op('uuid_ops'),
        ),
        warrantProgramsCompanyIdFkey: foreignKey({
          columns: [table.companyId],
          foreignColumns: [companies.id],
          name: 'warrant_programs_company_id_fkey',
        }),
        warrantProgramsContactIdentityIdFkey: foreignKey({
          columns: [table.contactId],
          foreignColumns: [identities.id],
          name: 'warrant_programs_contact_identity_id_fkey',
        }),
        warrantProgramsMeetingIdFkey: foreignKey({
          columns: [table.meetingId],
          foreignColumns: [meetings.id],
          name: 'warrant_programs_meeting_id_fkey',
        }),
        warrantProgramsSignatoryIdentityIdFkey: foreignKey({
          columns: [table.signatoryId],
          foreignColumns: [identities.id],
          name: 'warrant_programs_signatory_identity_id_fkey',
        }),
        warrantProgramsSnrAuthorizationIdFkey: foreignKey({
          columns: [table.snrAuthorizationId],
          foreignColumns: [snrAuthorizations.id],
          name: 'warrant_programs_snr_authorization_id_fkey',
        }),
        warrantProgramsSnrBondIdFkey: foreignKey({
          columns: [table.snrBondId],
          foreignColumns: [snrBonds.id],
          name: 'warrant_programs_snr_bond_id_fkey',
        }),
        warrantProgramsSnrCaseIdFkey: foreignKey({
          columns: [table.snrCaseId],
          foreignColumns: [snrCases.id],
          name: 'warrant_programs_snr_case_id_fkey',
        }),
        warrantProgramsValuationSccIdFkey: foreignKey({
          columns: [table.valuationSccId],
          foreignColumns: [snrShareCapitalChanges.id],
          name: 'warrant_programs_valuation_scc_id_fkey',
        }),
        warrantProgramsValuationShareIssueIdFkey: foreignKey({
          columns: [table.valuationShareIssueId],
          foreignColumns: [shareIssues.id],
          name: 'warrant_programs_valuation_share_issue_id_fkey',
        }),
      },
    ];
  },
);

export const warrantShareholders = pgTable(
  'warrant_shareholders',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    numberOfShares: bigint('number_of_shares', { mode: 'number' }).notNull(),
    warrantProgramId: uuid('warrant_program_id').notNull(),
    shareholderId: uuid('shareholder_id').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    proxyId: uuid('proxy_id'),
  },
  table => {
    return [
      {
        idxWshWpidShid: uniqueIndex('idx_wsh_wpid_shid').using(
          'btree',
          table.warrantProgramId.asc().nullsLast().op('uuid_ops'),
          table.shareholderId.asc().nullsLast().op('uuid_ops'),
        ),
        warrantShareholdersProxyIdentityIdFkey: foreignKey({
          columns: [table.proxyId],
          foreignColumns: [identities.id],
          name: 'warrant_shareholders_proxy_identity_id_fkey',
        }),
        warrantShareholdersShareholderIdFkey: foreignKey({
          columns: [table.shareholderId],
          foreignColumns: [shareholders.id],
          name: 'warrant_shareholders_shareholder_id_fkey',
        }),
        warrantShareholdersWarrantProgramIdFkey: foreignKey({
          columns: [table.warrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'warrant_shareholders_warrant_program_id_fkey',
        }),
      },
    ];
  },
);

export const warrantProgramParticipants = pgTable(
  'warrant_program_participants',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    delWarrantProgramId: uuid('_del_warrant_program_id'),
    quantity: numeric(),
    vestingKind: integer('vesting_kind').notNull(),
    vestingDuration: integer('vesting_duration').notNull(),
    vestingAcceleration: boolean('vesting_acceleration').notNull(),
    vestingCliffDuration: integer('vesting_cliff_duration').notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
    signatureKind: integer('signature_kind'),
    email: varchar(),
    identityId: uuid('identity_id').notNull(),
    shareClass: jsonb('share_class').$type<ShareClass>().notNull(),
    forcedRedemption: boolean('forced_redemption').default(false).notNull(),
    paymentStatus: integer('payment_status').notNull(),
    forcedSaleAtIpo: boolean('forced_sale_at_ipo').notNull(),
    vestingFrequency: integer('vesting_frequency').notNull(),
    repurchaseVested: boolean('repurchase_vested').notNull(),
    paymentKind: integer('payment_kind').notNull(),
    claim: numeric(),
    optionsRegistrationId: uuid('options_registration_id').notNull(),
    leaveOfAbsenceDays: integer('leave_of_absence_days').notNull(),
    vestingCustomKindPrimaryLang: text('vesting_custom_kind_primary_lang'),
    vestingCustomKindSecondaryLang: text('vesting_custom_kind_secondary_lang'),
    vestingEndDate: date('vesting_end_date'),
    leaveOfAbsence: boolean('leave_of_absence').notNull(),
    quantityRepurchased: numeric().notNull().default('0'),
    quantityInvalidated: numeric(),
  },
  table => {
    return [
      {
        idxWpsIdentityId: index('idx_wps_identity_id').using(
          'btree',
          table.identityId.asc().nullsLast().op('uuid_ops'),
        ),
        idxWpsOrId: index('idx_wps_or_id').using('btree', table.optionsRegistrationId.asc().nullsLast().op('uuid_ops')),
        indexWarrantProgramParticipantsWarrantProgramId: index(
          'index_warrant_program_participants_warrant_program_id',
        ).using('btree', table.delWarrantProgramId.asc().nullsLast().op('uuid_ops')),
        warrantProgramBuyersWarrantProgramIdFkey: foreignKey({
          columns: [table.delWarrantProgramId],
          foreignColumns: [warrantPrograms.id],
          name: 'warrant_program_buyers_warrant_program_id_fkey',
        }),
        warrantProgramParticipantsIdentityIdFkey: foreignKey({
          columns: [table.identityId],
          foreignColumns: [identities.id],
          name: 'warrant_program_participants_identity_id_fkey',
        }),
        warrantProgramParticipantsOptionsRegistrationIdFkey: foreignKey({
          columns: [table.optionsRegistrationId],
          foreignColumns: [optionsRegistrations.id],
          name: 'warrant_program_participants_options_registration_id_fkey',
        }),
      },
    ];
  },
);

export const warrantParticipantDocuments = pgTable(
  'warrant_participant_documents',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .notNull(),
    warrantParticipantId: uuid('warrant_participant_id').notNull(),
    fileId: uuid('file_id').notNull(),
    kind: text().notNull(),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        idxWppdFileid: uniqueIndex('idx_wppd_fileid').using('btree', table.fileId.asc().nullsLast().op('uuid_ops')),
        idxWppdWppid: index('idx_wppd_wppid').using(
          'btree',
          table.warrantParticipantId.asc().nullsLast().op('uuid_ops'),
        ),
        warrantParticipantDocumentsFileIdFkey: foreignKey({
          columns: [table.fileId],
          foreignColumns: [files.id],
          name: 'warrant_participant_documents_file_id_fkey',
        }),
        warrantParticipantDocumentsWarrantParticipantIdFkey: foreignKey({
          columns: [table.warrantParticipantId],
          foreignColumns: [warrantProgramParticipants.id],
          name: 'warrant_participant_documents_warrant_participant_id_fkey',
        }),
      },
    ];
  },
);

export const warrantParticipantVestingOccasions = pgTable(
  'warrant_participant_vesting_occasions',
  {
    id: uuid()
      .default(sql`public.gen_random_uuid()`)
      .primaryKey()
      .notNull(),
    warrantParticipantId: uuid('warrant_participant_id').notNull(),
    quantity: numeric().notNull(),
    vestingDate: date('vesting_date'),
    requirementPrimaryLang: text('requirement_primary_lang'),
    requirementSecondaryLang: text('requirement_secondary_lang'),
    descriptionPrimaryLang: text('description_primary_lang'),
    sortOrder: integer('sort_order').notNull(),
    descriptionSecondaryLang: text('description_secondary_lang'),
    createdAt: timestamp('created_at', { mode: 'string' }).defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { mode: 'string' }).defaultNow().notNull(),
  },
  table => {
    return [
      {
        warrantParticipantVestingOccasioWarrantParticipantIdFkey: foreignKey({
          columns: [table.warrantParticipantId],
          foreignColumns: [warrantProgramParticipants.id],
          name: 'warrant_participant_vesting_occasio_warrant_participant_id_fkey',
        }),
      },
    ];
  },
);

export type WarrantProgramParticipant = InferSelectModel<typeof warrantProgramParticipants>;
export type WarrantProgram = InferSelectModel<typeof warrantPrograms>;
export type WarrantParticipantVestingOccasion = InferSelectModel<typeof warrantParticipantVestingOccasions>;
